import React from 'react';
import { Controller, ControllerProps, FieldError, useFormContext } from 'react-hook-form';
import { formatDateTimezoneOffsetHotfix } from '@/tools/hotfix/formatDateTimezoneOffsetHotfix.ts';
import { DatePickerField } from '@schuettflix/planum-react';
import { cn } from '@/shared/utils/cn.ts';

type DateRangeFieldMapping = {
    // property name for start date
    readonly from: string;
    // property name for end date
    readonly to: string;
};

type MappedDateRange<T extends DateRangeFieldMapping> = {
    [K in T['from']]: string;
} & {
    [K in T['to']]: string;
};

const DEFAULT_RANGE_FIELDS = {
    from: 'validFrom',
    to: 'validTo',
} as const;

type DateRangeValue = Record<string, string>;

type SingleDatePickerFieldValues = { [key: string]: string };

type RangeDatePickerFieldValues = { [key: string]: DateRangeValue };

type DatePickerFieldValues = SingleDatePickerFieldValues | RangeDatePickerFieldValues;

type BaseDatePickerFieldInputProps = {
    className?: string;
    placeHolder?: string;
    size?: 'md' | 'lg';
    dataTest?: string;
    minDate?: string;
    maxDate?: string;
};

export type SingleDatePickerFieldInputProps = BaseDatePickerFieldInputProps &
    Pick<ControllerProps<SingleDatePickerFieldValues>, 'name' | 'defaultValue' | 'disabled'> & {
        mode: 'single';
        onDatePickerFieldChange?: (date: string | undefined) => void;
    };

type RangeDatePickerFieldInputProps<TFields extends DateRangeFieldMapping = typeof DEFAULT_RANGE_FIELDS> =
    BaseDatePickerFieldInputProps &
        Pick<ControllerProps<RangeDatePickerFieldValues>, 'name' | 'defaultValue' | 'disabled'> & {
            mode: 'range';
            rangeFields?: TFields;
            onDatePickerFieldChange?: (dateRange: MappedDateRange<TFields> | undefined) => void;
        };

export type DatePickerFieldInputProps<TFields extends DateRangeFieldMapping = typeof DEFAULT_RANGE_FIELDS> =
    | SingleDatePickerFieldInputProps
    | RangeDatePickerFieldInputProps<TFields>;

export const DatePickerFieldInput = React.forwardRef(
    <TFields extends DateRangeFieldMapping = typeof DEFAULT_RANGE_FIELDS>(
        props: DatePickerFieldInputProps<TFields>,
        _ref: React.Ref<HTMLInputElement>
    ) => {
        const {
            name = 'datePickerName',
            defaultValue,
            placeHolder,
            disabled,
            size = 'lg',
            minDate,
            maxDate,
            className,
            dataTest,
        } = props;

        const {
            control,
            formState: { errors },
        } = useFormContext<DatePickerFieldValues>();

        return (
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
                    if (props.mode === 'range') {
                        const fieldMapping = (props.rangeFields ?? DEFAULT_RANGE_FIELDS) as TFields;

                        // create transforms based on the field mapping
                        const transformToComponent = createFormToComponentTransform<TFields>(fieldMapping);
                        const transformFromComponent = createComponentToFormTransform<TFields>(fieldMapping);

                        const componentValue = transformToComponent(value);
                        const dateRangeFieldsErrors = errors[name] as Partial<Record<string, FieldError>>;

                        return (
                            <div ref={ref} tabIndex={-1} className={cn('focus:outline-none', className)}>
                                <DatePickerField
                                    {...field}
                                    className="w-full"
                                    data-test={dataTest}
                                    selected={componentValue}
                                    mode="range"
                                    minDate={parseDateValue(minDate)}
                                    maxDate={parseDateValue(maxDate)}
                                    onSelect={dateRange => {
                                        if (!dateRange?.from || !dateRange?.to) {
                                            props.onDatePickerFieldChange?.(undefined);
                                            onChange(undefined);
                                        } else {
                                            const formValue = transformFromComponent({
                                                from: dateRange.from,
                                                to: dateRange.to,
                                            });

                                            props.onDatePickerFieldChange?.(formValue);
                                            onChange(formValue);
                                        }
                                    }}
                                    size={size}
                                    placeholder={placeHolder}
                                    disabled={disabled}
                                    error={
                                        fieldState.error?.message ??
                                        dateRangeFieldsErrors?.[fieldMapping.from]?.message ??
                                        dateRangeFieldsErrors?.[fieldMapping.to]?.message
                                    }
                                />
                            </div>
                        );
                    } else {
                        const dateValue = parseDateValue(value);

                        return (
                            <div ref={ref} tabIndex={-1} className={cn('focus:outline-none', className)}>
                                <DatePickerField
                                    {...field}
                                    className="w-full"
                                    data-test={dataTest}
                                    selected={dateValue}
                                    mode="single"
                                    minDate={parseDateValue(minDate)}
                                    maxDate={parseDateValue(maxDate)}
                                    onSelect={date => {
                                        const dateValue = date ? formatDateTimezoneOffsetHotfix(date) : undefined;
                                        props.onDatePickerFieldChange?.(dateValue);
                                        onChange(dateValue);
                                    }}
                                    size={size}
                                    placeholder={placeHolder}
                                    disabled={disabled}
                                    error={fieldState.error?.message}
                                />
                            </div>
                        );
                    }
                }}
            />
        );
    }
) as <TFields extends DateRangeFieldMapping = typeof DEFAULT_RANGE_FIELDS>(
    props: DatePickerFieldInputProps<TFields> & { ref?: React.Ref<HTMLInputElement> }
) => React.ReactElement;

function isValidDateRangeFormValue(value: unknown, fieldMapping: DateRangeFieldMapping): value is DateRangeValue {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }

    const obj = value as Record<string, unknown>;
    const fromValue = obj[fieldMapping.from];
    const toValue = obj[fieldMapping.to];

    return !!fromValue && !!toValue;
}

// this function is responsible for transforming external props value
// to component format value
function createFormToComponentTransform<TFields extends DateRangeFieldMapping>(fieldMapping: TFields) {
    return (value: unknown): { from: Date; to: Date } | undefined => {
        if (!isValidDateRangeFormValue(value, fieldMapping)) {
            return undefined;
        }

        try {
            const fromValue = value[fieldMapping.from];
            const toValue = value[fieldMapping.to];

            const fromTime = Date.parse(fromValue);
            const toTime = Date.parse(toValue);

            if (isNaN(fromTime) || isNaN(toTime)) {
                return undefined;
            }

            const fromDate = new Date(fromTime);

            return {
                from: fromDate,
                to: toTime === fromTime ? fromDate : new Date(toTime),
            };
        } catch {
            return undefined;
        }
    };
}

// this function is responsible for transforming component format value
// to external props value
function createComponentToFormTransform<TFields extends DateRangeFieldMapping>(fieldMapping: TFields) {
    return (dateRange: { from: Date; to: Date }): MappedDateRange<TFields> => {
        return {
            [fieldMapping.from]: formatDateTimezoneOffsetHotfix(dateRange.from),
            [fieldMapping.to]: formatDateTimezoneOffsetHotfix(dateRange.to),
        } as MappedDateRange<TFields>;
    };
}

function parseDateValue(value: unknown): Date | undefined {
    if (typeof value !== 'string' || value.length === 0) {
        return undefined;
    }

    try {
        const time = Date.parse(value);
        return isNaN(time) ? undefined : new Date(time);
    } catch {
        return undefined;
    }
}
